import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import ModifiedTag from '@components/common/form/ModifiedField';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectRolesByNames } from '@redux/authent/authent.selectors';
import { getRoleAction, isBasicRole } from '@utils/authent/roles.utils';
import { haveSameUniqueElements } from '@utils/common.utils';
import { FormattedMessage } from 'react-intl';

import { Role, RoleEnum } from '@/types/authent/roles.types';

import { useProfileFormContext } from './ProfileFormContainer';

type Props = {
  subCategoryList: RoleEnum[];
  subCategoryName: string;
  skipTranslation: boolean;
};

export default function RoleSubCategory({ subCategoryName, subCategoryList, skipTranslation }: Readonly<Props>) {
  const roles = useAppSelector((state) => selectRolesByNames(state, subCategoryList));
  const { values, initialValues, setFieldValue } = useProfileFormContext();

  const hasChanged = !haveSameUniqueElements(
    initialValues.roles.filter((role) => subCategoryList.includes(role)),
    values.roles.filter((role) => subCategoryList.includes(role)),
  );

  const handleClick = (role: Role, isActive: boolean) => {
    if (isActive) {
      setFieldValue(
        'roles',
        values.roles.filter((e) => e !== role.roleName),
      );
    } else {
      setFieldValue('roles', values.roles.concat(role.roleName));
    }
  };

  return (
    <VStack paddingY={4} gap={2} width="100%" alignItems="start">
      <HStack width="100%">
        <Text lineHeight="20px">
          <FormattedMessage id={`components.usersManagement.profiles.roles.subCategory.${subCategoryName}`} />
        </Text>
        {hasChanged && <ModifiedTag />}
      </HStack>
      <HStack width="100%" gap={1} flexWrap="wrap">
        {roles
          .toSorted((a, b) => subCategoryList.indexOf(a.roleName) - subCategoryList.indexOf(b.roleName))
          .map((role) => {
            const action = getRoleAction(role);
            const isActive = values.roles.some((e) => e === role.roleName);
            return (
              <Button
                variant="toggle"
                width="min-content"
                minWidth="120px"
                key={role.roleName}
                size="toggleM"
                disabled={isBasicRole(role) && isActive}
                data-active={isActive}
                onClick={() => handleClick(role, isActive)}
              >
                {skipTranslation ? (
                  role.roleName
                ) : (
                  <FormattedMessage
                    id={
                      action
                        ? `components.usersManagement.profiles.roles.action.${action}`
                        : `components.usersManagement.profiles.roles.label.${role.roleName}`
                    }
                  />
                )}
              </Button>
            );
          })}
      </HStack>
    </VStack>
  );
}
